<script setup lang="ts">
interface Props {
  recipeId: number
}

const props = defineProps<Props>()

const config = useRuntimeConfig()
const userStore = useUserStore()

const recipeScore = ref<any>(false)
const loading = ref(false)
const error = ref(false)

// Validate recipe ID
const isValidRecipeId = computed(() => {
  return props.recipeId && typeof props.recipeId === 'number' && props.recipeId > 0
})

// Fonctions pour le gauge moderne
function getGaugeValue(score: number): number {
  return (score / 100) * 251.33 // 251.33 est la circonférence du cercle (2 * π * 40)
}

function getGaugeColor(score: number): string {
  if (score >= 90)
    return '#06b6d4' // Cyan pour excellent (90-100)
  if (score >= 80)
    return '#10b981' // Vert émeraude pour très bon (80-89)
  if (score >= 70)
    return '#84cc16' // Vert lime pour bon (70-79)
  if (score >= 60)
    return '#eab308' // Jaune pour moyen-bon (60-69)
  if (score >= 50)
    return '#f97316' // Orange pour moyen (50-59)
  if (score >= 40)
    return '#ea580c' // Orange foncé pour moyen-faible (40-49)
  if (score >= 30)
    return '#dc2626' // Rouge pour faible (30-39)
  if (score >= 20)
    return '#b91c1c' // Rouge foncé pour très faible (20-29)
  return '#991b1b' // Rouge très foncé pour très mauvais (0-19)
}

function getScoreLabel(score: number): string {
  if (score >= 90)
    return 'WOW!'
  if (score >= 80)
    return 'Excellent'
  if (score >= 70)
    return 'Bon'
  if (score >= 60)
    return 'Moyen'
  if (score >= 50)
    return 'Moyen'
  if (score >= 40)
    return 'Moyen'
  if (score >= 30)
    return 'Faible'
  if (score >= 20)
    return 'Faible'
  return 'Très faible'
}

function getScoreClass(score: number): string {
  if (score >= 90)
    return 'bg-cyan-500 shadow-cyan-500/20'
  if (score >= 80)
    return 'bg-emerald-500 shadow-emerald-500/20'
  if (score >= 70)
    return 'bg-lime-500 shadow-lime-500/20'
  if (score >= 60)
    return 'bg-yellow-500 shadow-yellow-500/20'
  if (score >= 50)
    return 'bg-orange-500 shadow-orange-500/20'
  if (score >= 40)
    return 'bg-orange-600 shadow-orange-600/20'
  if (score >= 30)
    return 'bg-red-600 shadow-red-600/20'
  if (score >= 20)
    return 'bg-red-700 shadow-red-700/20'
  return 'bg-red-800 shadow-red-800/20'
}

function getScoreDescription(score: number): string {
  if (score >= 90)
    return 'Économies exceptionnelles possibles'
  if (score >= 80)
    return 'Économies très importantes possibles'
  if (score >= 70)
    return 'Économies importantes possibles'
  if (score >= 60)
    return 'Bonnes économies possibles'
  if (score >= 50)
    return 'Économies modérées possibles'
  if (score >= 40)
    return 'Économies limitées possibles'
  if (score >= 30)
    return 'Peu d\'économies possibles'
  if (score >= 20)
    return 'Très peu d\'économies possibles'
  return 'Presque aucune économie possible'
}

async function fetchScore(id: number) {
  if (!isValidRecipeId.value) {
    console.error('Invalid recipe ID provided to RecipeScoreBlock:', id)
    error.value = true
    return
  }

  try {
    loading.value = true
    error.value = false

    const response = await $fetch(`${config.public.apiUrl}/promos/get-recipe-score`, {
      method: 'GET',
      params: { id },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    }) as any

    if (response?.success === 1) {
      recipeScore.value = response
    }
    else {
      console.error('API returned error for recipe score:', response)
      error.value = true
    }
  }
  catch (err) {
    console.error('Erreur lors de la récupération du score:', err)
    error.value = true
  }
  finally {
    loading.value = false
  }
}

onMounted(() => {
  if (isValidRecipeId.value) {
    fetchScore(props.recipeId)
  }
  else {
    console.error('RecipeScoreBlock: Invalid recipe ID provided:', props.recipeId)
    error.value = true
  }
})

// Watch for changes in recipe ID
watch(() => props.recipeId, (newId) => {
  if (newId && isValidRecipeId.value) {
    fetchScore(newId)
  }
})
</script>

<template>
  <!-- Error state -->
  <div v-if="error" class="text-center p-4">
    <UIcon name="i-mdi-alert-circle" class="text-2xl text-red-400 mb-2" />
    <p class="text-sm text-poivre-500">
      Impossible de calculer l'indice d'économie
    </p>
  </div>

  <!-- Loading state -->
  <div v-else-if="loading" class="flex items-center justify-center pt-4">
    <UIcon name="i-mdi-loading" class="animate-spin text-2xl text-poivre-400" />
    <span class="ml-2 text-poivre-500">Calcul de l'indice...</span>
  </div>

  <!-- Success state -->
  <div v-else-if="recipeScore" class="text-center pt-4 px-2">
    <!-- Indicateurs visuels -->
    <div class="mt-0">
      <div class="flex items-center justify-center gap-2">
        <div class="w-3 h-3 rounded-full flex-shrink-0 shadow-[0_0_0_3px_rgba(0,0,0,0.2)]" :class="getScoreClass(recipeScore.score)" />
        <span class="text-sm font-medium text-gray-700">{{ getScoreDescription(recipeScore.score) }}</span>
      </div>
    </div>
  </div>

  <!-- Default state -->
  <div v-else class="flex items-center justify-center p-8">
    <UIcon name="i-mdi-loading" class="animate-spin text-2xl text-poivre-400" />
    <span class="ml-2 text-poivre-500">Calcul en cours...</span>
  </div>

  <div class="p-4 text-sm text-poivre-500 leading-4">
    Le potentiel d'économies est calculé en combinant les rabais des {{ userStore.user?.stores.length }} commerces favoris à votre profil.
  </div>
</template>

<style scoped>
/* Styles spécifiques pour les couleurs dynamiques des indicateurs */
.shadow-cyan-500\/20 {
  box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.2);
}

.shadow-emerald-500\/20 {
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
}

.shadow-lime-500\/20 {
  box-shadow: 0 0 0 3px rgba(132, 204, 22, 0.2);
}

.shadow-yellow-500\/20 {
  box-shadow: 0 0 0 3px rgba(234, 179, 8, 0.2);
}

.shadow-orange-500\/20 {
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.2);
}

.shadow-orange-600\/20 {
  box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.2);
}

.shadow-red-600\/20 {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.2);
}

.shadow-red-700\/20 {
  box-shadow: 0 0 0 3px rgba(185, 28, 28, 0.2);
}

.shadow-red-800\/20 {
  box-shadow: 0 0 0 3px rgba(153, 27, 27, 0.2);
}
</style>
